import client from '../../apollo';
import { loadQueue, reloadView } from './actions';
import { selectQueueView } from './selectors';
import activateOnboardingQueueGroupMutation from '../../graphql/activateOnboardingQueueGroupMutation';
import activateOnboardingQueueRecordMutation from '../../graphql/activateOnboardingQueueRecordMutation';
import addOnboardingQueueNoteMutation from '../../graphql/addOnboardingQueueNoteMutation';
import editOnboardingQueueRecordStatusMutation from '../../graphql/editOnboardingQueueRecordStatusMutation';
import externalOnboardingQueueRecordQuery from '../../graphql/externalOnboardingQueueRecordQuery';
import optOutOnboardingQueueGroupMutation from '../../graphql/optOutOnboardingQueueGroupMutation';
import optOutOnboardingQueueRecordMutation from '../../graphql/optOutOnboardingQueueRecordMutation';
import { mutationErrorMessage } from '../../utilities/onboardingQueue';
import {
  ONBOARDING_REASON,
  QUEUE_PAGE_SIZE,
  QUEUE_REFUSED_MESSAGE,
  QUEUE_STATUS,
} from '../../constants/onboardingQueue';

const failure = (payload, error) => ({
  success: false,
  message: mutationErrorMessage(payload, error),
  data: null,
});

const graphQLMessages = source => {
  const errors = source && Array.isArray(source.graphQLErrors) ? source.graphQLErrors : [];
  return errors.map(item => item && item.message).filter(Boolean);
};

const runMutation = async(mutation, variables, field, dataField = 'onboardingQueueRecord') => {
  try {
    const { data } = await client.mutate({ mutation, variables });
    const payload = data ? data[field] : null;
    if (!payload || !payload.success) return failure(payload, null);
    return { success: true, message: null, data: payload[dataField] || null };
  } catch (error) {
    return failure({ errors: graphQLMessages(error) }, error);
  }
};

export const fetchRecordDetail = flatRateOfferId => async() => {
  try {
    const { data, errors } = await client.query({
      query: externalOnboardingQueueRecordQuery,
      variables: { flatRateOfferId },
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    });
    const record = data ? data.externalOnboardingQueueRecord : null;
    if (record) return { success: true, message: null, data: record };

    const messages = errors ? errors.map(item => item && item.message).filter(Boolean) : [];
    if (messages.length === 0) {
      return { success: false, message: QUEUE_REFUSED_MESSAGE, data: null };
    }
    return failure({ errors: messages }, null);
  } catch (error) {
    return failure({ errors: graphQLMessages(error) }, error);
  }
};

export const activateRecord = ({ flatRateOfferId }) => async() => runMutation(
  activateOnboardingQueueRecordMutation,
  { input: { flatRateOfferId } },
  'activateOnboardingQueueRecord'
);

export const activateGroup = ({ flatRateOfferIds }) => async() => runMutation(
  activateOnboardingQueueGroupMutation,
  { input: { onboardingQueueGroupActivate: { flatRateOfferIds } } },
  'activateOnboardingQueueGroup',
  'onboardingQueueGroupAction'
);

export const optOutRecord = ({ flatRateOfferId, reason, otherText }) => async() => runMutation(
  optOutOnboardingQueueRecordMutation,
  {
    input: {
      flatRateOfferId,
      onboardingQueueOptOut: reason === ONBOARDING_REASON.OTHER
        ? { reason, otherText }
        : { reason },
    },
  },
  'optOutOnboardingQueueRecord'
);

export const optOutGroup = ({ flatRateOfferIds, reason, otherText }) => async() => runMutation(
  optOutOnboardingQueueGroupMutation,
  {
    input: {
      onboardingQueueGroupOptOut: reason === ONBOARDING_REASON.OTHER
        ? { flatRateOfferIds, reason, otherText }
        : { flatRateOfferIds, reason },
    },
  },
  'optOutOnboardingQueueGroup',
  'onboardingQueueGroupAction'
);

export const editRecordStatus = ({
  flatRateOfferId, status, reason, otherText,
}) => async() => {
  const editStatus = { status };
  if (status !== QUEUE_STATUS.PENDING) {
    editStatus.reason = reason;
    if (reason === ONBOARDING_REASON.OTHER) editStatus.otherText = otherText;
  }

  return runMutation(
    editOnboardingQueueRecordStatusMutation,
    { input: { flatRateOfferId, onboardingQueueEditStatus: editStatus } },
    'editOnboardingQueueRecordStatus'
  );
};

export const addRecordNote = ({ flatRateOfferId, note }) => async() => runMutation(
  addOnboardingQueueNoteMutation,
  { input: { flatRateOfferId, note } },
  'addOnboardingQueueNote'
);

export const refetchBoth = ({ filters, viewStates }) => dispatch => (
  dispatch(loadQueue({ filters, viewStates }))
);

export const refetchActiveView = ({
  filters, view, sort, order, page,
}) => dispatch => dispatch(reloadView({
  filters, view, sort, order, page,
}));

export const settleActivePage = ({
  filters, view, sort, order, page,
}) => async(dispatch, getState) => {
  const { items, totalRows } = selectQueueView(getState(), view);
  if (items.length > 0 || totalRows === 0 || page === 0) return page;

  const lastPage = Math.max(Math.ceil(totalRows / QUEUE_PAGE_SIZE) - 1, 0);
  const settled = Math.min(page, lastPage);
  await dispatch(reloadView({
    filters, view, sort, order, page: settled,
  }));
  return settled;
};
